import { TeacherCertificationService } from 'src/app/services/teacher-certification.service';
import { DeaSubjectsListingDialogComponentOutput } from 'src/app/constants/interfaces';
import { BaseComponentService } from 'src/app/services/_app/base-component.service';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SubjectService } from 'src/app/services/subject.service';
import { ApiError } from 'src/app/models/api/api-error.model';
import { Component, Inject, inject } from '@angular/core';
import { AppTools } from 'src/app/utils/tools';

@Component({
  selector: 'app-teacher-certification-dialog',
  templateUrl: './teacher-certification-dialog.component.html',
})
export class TeacherCertificationDialogComponent {
  private _teacherCertificationService = inject(TeacherCertificationService);
  private _subjectService: SubjectService = inject(SubjectService);
  private _baseComponentService = inject(BaseComponentService);

  protected error?: ApiError;
  protected teacherId?: string;
  protected isPosting: boolean = false;
  protected teacherCertificationId?: string;

  protected formGroup: FormGroup = new FormGroup({
    user: new FormControl(),
    year: new FormControl(null, Validators.required),
    teaching_subjects: new FormArray([]),
    description: new FormControl(),
    file: new FormControl(null),
  });

  protected get teachingSubjectsArray(): FormArray {
    return this.formGroup.get('teaching_subjects') as FormArray;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    protected data: { teacherId?: string; teacherCertificationId?: string },
    protected _dialogRef: MatDialogRef<TeacherCertificationDialogComponent>
  ) {
    this.teacherId = this.data.teacherId;
    if (this.teacherId) this.formGroup.patchValue({ user: this.teacherId });
    this.teacherCertificationId = this.data.teacherCertificationId;
    if (this.teacherCertificationId) {
      this.formGroup.get('user')?.disable();
      this._getTeacherCertificationDetails();
    }
  }

  private async _getTeacherCertificationDetails() {
    this.formGroup.disable();
    this._teacherCertificationService
      .getTeacherCertificationDetails(this.teacherCertificationId)
      .subscribe({
        next: (response) => {
          this.formGroup.patchValue({ ...response.data });
          response.data?.teaching_subjects?.map((s) => {
            (this.formGroup.get('teaching_subjects') as FormArray).push(
              new FormGroup({
                _id: new FormControl(s.id),
                nome: new FormControl(s.nome),
                dea_id: new FormControl(s.dea_id),
              })
            );
          });
        },
        error: (error) => {
          this._baseComponentService.showErrorDialog({ error });
          this._dialogRef.close(false);
        },
      })
      .add(() => this.formGroup.enable());
  }

  protected openSubjectsDialog() {
    this._subjectService
      .openSubjectsDialog()
      .afterClosed()
      .subscribe((subject: DeaSubjectsListingDialogComponentOutput) => {
        if (subject.id) {
          (this.formGroup.get('teaching_subjects') as FormArray).push(
            new FormGroup({
              _id: new FormControl(subject.id),
              nome: new FormControl(subject.nome),
              dea_id: new FormControl(subject.dea_id),
            })
          );
        }
      });
  }

  protected removeSubjectById(subjectId?: string): void {
    if (!subjectId) return;
    const formArray = this.formGroup.get('teaching_subjects') as FormArray;
    const index = formArray.controls.findIndex((group) => {
      return subjectId === group.get('_id')?.value;
    });
    if (index !== -1) formArray.removeAt(index);
  }

  protected onFileChange(event: any) {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      this.formGroup?.patchValue({ file: file });
    }
  }

  protected onConfirm() {
    this.isPosting = true;
    this.error = undefined;
    if (this.formGroup.valid === false) {
      this.error = new ApiError({
        message: 'Compila tutti i campi non contrassegnati come opzionali.',
      });
      this.isPosting = false;
      return;
    }
    const formValue = this.formGroup.value;
    const data = AppTools.removeNullAndReplaceWithUndefined({ ...formValue });
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      if (data[key] !== null && data[key] !== undefined) {
        if (key === 'teaching_subjects' && Array.isArray(data[key])) {
          if (data[key].length == 0) formData.append(`${key}`, '');
          else data[key].forEach((s) => formData.append(`${key}[]`, s._id));
        } else formData.append(key, data[key]);
      }
    });
    const file: any = this.formGroup.get('file')?.value;
    if (file) {
      if (formData.has('file')) formData.delete('file');
      formData.append(
        'file',
        file,
        file.name || new Date().toISOString().slice(0, 10)
      );
    }
    console.info('FormData:', formData);
    this.isPosting = false;
    if (this.teacherCertificationId) {
      this._teacherCertificationService
        .patchTeacherCertification(this.teacherCertificationId, formData)
        .subscribe({
          next: () => this._dialogRef.close(true),
          error: (error: ApiError) => {
            this._baseComponentService.showErrorDialog({ error });
          },
        })
        .add(() => (this.isPosting = false));
    } else {
      this._teacherCertificationService
        .postTeacherCertification(formData)
        .subscribe({
          next: (response) => {
            console.info('Post Teacher Certification:', response.data);
            this._dialogRef.close(true);
          },
          error: (error: ApiError) => {
            this._baseComponentService.showErrorDialog({ error });
          },
        });
    }
  }

  protected onDismiss(): void {
    this._dialogRef.close(false);
  }
}
